import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChartBarIcon,
  ServerIcon,
  Cog6ToothIcon,
  CurrencyDollarIcon,
  ChevronDownIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  CodeBracketIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline';
import { FiGrid, FiFileText, FiCheckSquare, FiBook, FiCpu, FiActivity, FiGitCommit, FiBarChart2, FiDatabase, FiZap } from 'react-icons/fi';
import { HiShieldCheck, HiCheckCircle, HiGlobeAlt, HiClipboardList } from 'react-icons/hi';
import { FaGithub } from 'react-icons/fa';
import { useStatus } from '../contexts/StatusProvider';
import { useBrand, BRAND_COLORS } from '@equilateral/ui-shared';
import NotificationToast from './NotificationToast';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  name: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface DropdownProps {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavItem[];
  isActive: boolean;
}

// Dropdown component for grouped navigation
const NavDropdown: React.FC<DropdownProps> = ({ label, icon: Icon, items, isActive }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`${
          isActive
            ? 'border-indigo-500 text-gray-900'
            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
        } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-full`}
      >
        <Icon className="w-5 h-5 mr-1" />
        {label}
        <ChevronDownIcon className={`w-4 h-4 ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
          <div className="py-1">
            {items.map((item) => {
              const ItemIcon = item.icon;
              const isItemActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={`${
                    isItemActive
                      ? 'bg-indigo-50 text-indigo-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  } flex items-center px-4 py-2 text-sm`}
                >
                  <ItemIcon className="w-4 h-4 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const { Is_Super_Admin } = useStatus();
  const { brandCode } = useBrand();
  const brandInfo = BRAND_COLORS[brandCode] || BRAND_COLORS.equilateral;
  const brandName = brandInfo.name;
  const brandColor = brandInfo.primary;

  // Set page title to brand name
  useEffect(() => {
    document.title = `${brandName} Platform`;
  }, [brandName]);

  const isActive = (path: string) => location.pathname === path;
  const isInGroup = (paths: string[]) => paths.some(p => location.pathname === p);

  // Primary workflow navigation (what users do daily)
  const workflowNav: NavItem[] = [
    { name: 'Dashboard', path: '/dashboard', icon: ChartBarIcon },
    { name: 'Boards', path: '/boards', icon: FiGrid },
    { name: 'Specs', path: '/specs', icon: FiBook },
    { name: 'Standards', path: '/standards', icon: FiFileText },
    { name: 'Reviews', path: '/reviews', icon: FiCheckSquare },
  ];

  // Platform Intelligence (AI-first features from v3 vision)
  const intelligenceNav: NavItem[] = [
    { name: 'Models', path: '/model-config', icon: FiCpu },
    { name: 'Costs', path: '/costs', icon: CurrencyDollarIcon },
    { name: 'Agents', path: '/agents', icon: ServerIcon },
    { name: 'Analytics', path: '/analytics', icon: FiBarChart2 },
    { name: 'Memory', path: '/memory', icon: FiDatabase },
    { name: 'Inference', path: '/inference', icon: FiZap },
  ];

  // Operations (technical/occasional use)
  const operationsNav: NavItem[] = [
    { name: 'Sessions', path: '/sessions', icon: ClockIcon },
    { name: 'Incidents', path: '/incidents', icon: ExclamationTriangleIcon },
    { name: 'Projects', path: '/projects', icon: FolderIcon },
    { name: 'Repositories', path: '/repositories', icon: CodeBracketIcon },
    { name: 'Executions', path: '/workflow-inspector', icon: MagnifyingGlassIcon },
    { name: 'Assessments', path: '/assessments', icon: HiShieldCheck },
    { name: 'Std Profiles', path: '/standards/profiles', icon: AdjustmentsHorizontalIcon },
    { name: 'Audit Log', path: '/audit-log', icon: HiClipboardList },
  ];

  // Governance (cross-entity dispatch governance — EQ-PROV-007)
  const governanceNav: NavItem[] = [
    { name: 'Entity Topology', path: '/governance/topology', icon: HiGlobeAlt },
    { name: 'Touchpoints', path: '/governance/touchpoints', icon: FiActivity },
    { name: 'Workflow Sim', path: '/governance/workflow', icon: FiGrid },
    { name: 'Fed Dashboard', path: '/governance/dashboard', icon: ChartBarIcon },
    { name: 'Governance P3', path: '/governance-p3', icon: HiShieldCheck },
  ];

  // Settings navigation (integrations and configuration)
  const settingsNav: NavItem[] = [
    { name: 'General', path: '/settings', icon: Cog6ToothIcon },
    { name: 'GitHub App', path: '/settings/github-app', icon: FaGithub },
    { name: 'Platform Commits', path: '/settings/platform-commits', icon: FiGitCommit },
    { name: 'Commit Approvals', path: '/settings/commit-approvals', icon: HiCheckCircle },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              {/* Logo */}
              <div className="flex-shrink-0 flex items-center">
                <Link to="/dashboard" className="text-xl font-bold" style={{ color: brandColor }}>
                  {brandName}
                </Link>
              </div>

              {/* Primary Navigation */}
              <div className="hidden sm:ml-6 sm:flex sm:space-x-6">
                {/* Workflow Items (primary) */}
                {workflowNav.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`${
                        isActive(item.path)
                          ? 'border-indigo-500 text-gray-900'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                    >
                      <Icon className="w-5 h-5 mr-1.5" />
                      {item.name}
                    </Link>
                  );
                })}

                {/* Divider */}
                <div className="flex items-center">
                  <div className="h-6 w-px bg-gray-200" />
                </div>

                {/* Intelligence Dropdown */}
                <NavDropdown
                  label="Intelligence"
                  icon={FiActivity}
                  items={intelligenceNav}
                  isActive={isInGroup(intelligenceNav.map(i => i.path))}
                />

                {/* Operations Dropdown */}
                <NavDropdown
                  label="Operations"
                  icon={ServerIcon}
                  items={operationsNav}
                  isActive={isInGroup(operationsNav.map(i => i.path))}
                />

                {/* Governance Dropdown */}
                <NavDropdown
                  label="Governance"
                  icon={HiGlobeAlt}
                  items={governanceNav}
                  isActive={isInGroup(governanceNav.map(i => i.path))}
                />
              </div>
            </div>

            {/* Right side: Notifications, Settings & Admin */}
            <div className="hidden sm:ml-6 sm:flex sm:items-center sm:space-x-4">
              <NotificationToast />
              {Is_Super_Admin && (
                <Link
                  to="/super/dashboard"
                  className={`${
                    location.pathname.startsWith('/super')
                      ? 'text-indigo-600'
                      : 'text-gray-500 hover:text-gray-700'
                  } inline-flex items-center text-sm font-medium`}
                >
                  <HiShieldCheck className="w-5 h-5 mr-1" />
                  Admin
                </Link>
              )}
              {/* Settings Dropdown */}
              <NavDropdown
                label="Settings"
                icon={Cog6ToothIcon}
                items={settingsNav}
                isActive={location.pathname.startsWith('/settings')}
              />
            </div>
          </div>
        </div>

        {/* Mobile menu - simplified */}
        <div className="sm:hidden border-t border-gray-200">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {workflowNav.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`${
                    isActive(item.path)
                      ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                      : 'border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                  } block pl-3 pr-4 py-2 border-l-4 text-base font-medium flex items-center`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
            <div className="border-t border-gray-200 my-2" />
            <div className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase">Intelligence</div>
            {intelligenceNav.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`${
                    isActive(item.path)
                      ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                      : 'border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                  } block pl-3 pr-4 py-2 border-l-4 text-base font-medium flex items-center`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
            <div className="border-t border-gray-200 my-2" />
            <div className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase">Operations</div>
            {operationsNav.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`${
                    isActive(item.path)
                      ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                      : 'border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                  } block pl-3 pr-4 py-2 border-l-4 text-base font-medium flex items-center`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
            <div className="border-t border-gray-200 my-2" />
            <div className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase">Governance</div>
            {governanceNav.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`${
                    isActive(item.path)
                      ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                      : 'border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                  } block pl-3 pr-4 py-2 border-l-4 text-base font-medium flex items-center`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
            <div className="border-t border-gray-200 my-2" />
            <div className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase">Settings</div>
            {settingsNav.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`${
                    isActive(item.path) || (item.path !== '/settings' && location.pathname.startsWith(item.path))
                      ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                      : 'border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                  } block pl-3 pr-4 py-2 border-l-4 text-base font-medium flex items-center`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </div>
        </div>
      </nav>
      <main role="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};

export default Layout;
